import React from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from 'recharts';

import { ReactComponent as CalendarTickReserved } from '../../../assets/icons/calendar-tick-reserved.svg';
import { ReactComponent as CarFreeIcon } from '../../../assets/icons/car-free-icon.svg';
import { ReactComponent as CarUnAvailable } from '../../../assets/icons/car-un-available.svg';
import { ReactComponent as CarIconPrimary } from '../../../assets/icons/car.svg';
import { ReactComponent as DangerfaultIcon } from '../../../assets/icons/danger-fault-icon.svg';
import { ReactComponent as DeviceFinishing } from '../../../assets/icons/device-finishing.svg';
import { ReactComponent as DevicePreparing } from '../../../assets/icons/device-preparing.svg';
import { ReactComponent as DeviceReserved } from '../../../assets/icons/device-reserved.svg';
import { ReactComponent as DeviceSuspendedev } from '../../../assets/icons/device-suspendedev.svg';
import { ReactComponent as DeviceUnAvailable } from '../../../assets/icons/device-un-available.svg';
import { ReactComponent as DevicePictonIcon } from '../../../assets/icons/devicePictonIcon.svg';
import { ReactComponent as DeviceRedIcon } from '../../../assets/icons/deviceRedIcon.svg';
import { ReactComponent as DeviceYellowIcon } from '../../../assets/icons/deviceYellowIcon.svg';
import { ReactComponent as ElectricityFinishing } from '../../../assets/icons/electricity-finishing.svg';
import { ReactComponent as SlashSuspendedev } from '../../../assets/icons/slash-suspendedev.svg';
import { ReactComponent as TimePreparing } from '../../../assets/icons/timer-preparing.svg';

import DashedLineComponent from './animation-components/DashedLineComponent';
import DashedRing from './animation-components/DashedRing';

interface DeviceData {
  _id?: string;
  connectorId?: number;
  status: string;
  userCustomId?: string;
  vehicleManufacturer?: string;
  vehicleModel?: string;
  value?: number;
}
interface DeviceCardProperties {
  borderClass: string;
  title: string;
  allocated: string | number;
  available: string | number;
  used: string | number;
  array: DeviceData[];
}

/**
 * Normalize status: lowercase & remove spaces.
 * @param {any} value - The value
 */
const normalizeStatus = (value: string): string => value.toLowerCase().replace(/\s+/g, '');

/**
 * side panel component
 * @param {any} param0 - The param0
 * @param {any} param0.borderClass - The borderClass prop
 * @param {any} param0.title - The title prop
 * @param {any} param0.allocated - The allocated prop
 * @param {any} param0.available - The available prop
 * @param {any} param0.used - The used prop
 * @param {any} param0.array - The array prop
 */
function DeviceCard({
  borderClass,
  title,
  allocated,
  available,
  used,
  array,
}: DeviceCardProperties) {
  let images: JSX.Element = <div />;
  let color = '';
  const { t } = useTranslation('vlbc');
  const sanitizedClass = borderClass?.toLowerCase().replace(/\s+/g, '');

  if (sanitizedClass === 'inprogress') {
    color = '#38AAE7';
    images = <DevicePictonIcon />;
  } else if (sanitizedClass === 'fault') {
    color = '#E41010';
    images = <DeviceRedIcon />;
  } else if (sanitizedClass === 'free') {
    color = '#FFD000';
    images = <DeviceYellowIcon />;
  } else if (sanitizedClass === 'unavailable') {
    color = '#6C757D';
    images = <DeviceUnAvailable />;
  } else if (sanitizedClass === 'finishing') {
    color = '#00A79D';
    images = <DeviceFinishing />;
  } else if (sanitizedClass === 'preparing') {
    color = '#F79009';
    images = <DevicePreparing />;
  } else if (sanitizedClass === 'reserved') {
    color = '#4ABDFB';
    images = <DeviceReserved />;
  } else if (sanitizedClass === 'suspendedev' || sanitizedClass === 'suspendedevse') {
    color = '#B3261E';
    images = <DeviceSuspendedev />;
  }

  /**
   * Side panel component
   * @param {any} status - The status
   */
  const getStatusLabel = (status: string) => {
    const normalized = normalizeStatus(status);
    if (normalized === 'free') {
      return <span>{t('free')}</span>;
    }
    if (normalized === 'inprogress') {
      return <span>{t('inProgress')}</span>;
    }
    if (normalized === 'fault') {
      return <span>{t('fault')}</span>;
    }
    if (normalized === 'unavailable') {
      return <span>{t('unAvailable')}</span>;
    }
    if (normalized === 'finishing') {
      return <span>{t('finishing')}</span>;
    }
    if (normalized === 'preparing') {
      return <span>{t('preparing')}</span>;
    }
    if (normalized === 'reserved') {
      return <span>{t('reserved')}</span>;
    }
    if (normalized === 'suspendedev') {
      return <span>{t('suspendedEV')}</span>;
    }
    if (normalized === 'suspendedevse') {
      return <span>{t('suspendedEVSE')}</span>;
    }

    return null;
  };

  const validStatuses = [
    'inprogress',
    'fault',
    'unavailable',
    'free',
    'finishing',
    'preparing',
    'reserved',
    'suspendedev',
    'suspendedevse',
  ] as const;
  type Status = (typeof validStatuses)[number];

  /**
   * Returns a color based on the status value.
   * @param {any} value - The status string: "charging", "fault", or "unAvailable".
   * @returns {any} A color string.
   */
  const isStatus = (value: string): value is Status => {
    const normalized = normalizeStatus(value);
    return validStatuses.includes(normalized as Status);
  };

  /**
   * Determines whether animation should be applied based on the given status.
   * @param {string} status - The status string (e.g., 'charging', 'fault', 'unAvailable').
   * @returns {boolean} True if the status should trigger animation; otherwise, false.
   */
  const shouldAnimateStatus = (status: string): boolean => {
    const normalized = normalizeStatus(status);
    return normalized === 'inprogress';
  };

  /**
   * Returns a color based on the status value.
   * @param {any} status - The status string: "charging", "fault", or "unAvailable".
   * @returns {any} A color string.
   */
  const getColor = (status: string): string => {
    const normalized = normalizeStatus(status);
    if (isStatus(normalized)) {
      const colorMap: Record<Status, string> = {
        inprogress: '#E9ECEF',
        fault: '#E41010',
        unavailable: '#6C757D',
        free: '#FCE017',
        finishing: '#00A79D',
        preparing: '#F79009',
        reserved: '#4ABDFB',
        suspendedev: '#B3261E',
        suspendedevse: '#B3261E',
      };
      return colorMap[normalized];
    }
    return '#E9ECEF'; // fallback
  };

  const firstThree = array?.slice(0, 1);
  const firsttwo = array?.slice(1, 2);

  return (
    <div className="vlbc-management-device-card">
      <div className="device-card-container">
        <div
          className={`device-card-border device-card-border-${normalizeStatus(borderClass ?? '')}`}>
          <div className="device-card-ring">
            <DashedRing
              size={160}
              thickness={2}
              dashColor={color}
              activeColor="#00c389"
              dashCount={20}
              animationDuration={10}
              isAnimating={false} // <-- just include it without = {true}
              className=""
            />
          </div>
          <div className="device-card-icon">{images}</div>
          <div className="device-card-info">
            <div className="device-card-label">{title ?? ''}</div>
            <div className="device-card-label">
              {t('allocated')}{' '}
              <span className={`label-${normalizeStatus(borderClass ?? '')}`}>
                {allocated} AMPS
              </span>
            </div>
            <div className="device-card-label">
              {t('available')}{' '}
              <span className={`label-${normalizeStatus(borderClass ?? '')}`}>
                {available} AMPS{' '}
              </span>
            </div>
            <div className="device-card-label">
              {t('use')}{' '}
              <span className={`label-${normalizeStatus(borderClass ?? '')}`}>{used} AMPS</span>
            </div>
          </div>
        </div>
        {array.length === 1 &&
          firstThree?.map((item) => (
            <div className="single-connect-horizontal-left">
              <DashedLineComponent
                color={getColor(normalizeStatus(item.status))}
                width="5px"
                height="200px"
                dashSpacing="10px"
                shape="horizontal"
                animate={shouldAnimateStatus(normalizeStatus(item.status))}
                animationDelay={20}
                animationColor="#38AAE7"
                dotsCountValue={6}
              />
            </div>
          ))}

        {array.length === 2 &&
          firstThree?.map((item) => (
            <>
              <div key={item._id} className="horizontal">
                <DashedLineComponent
                  color={getColor(normalizeStatus(item.status))}
                  width="5px"
                  height="30px"
                  dashSpacing="10px"
                  shape="horizontal"
                  animate={shouldAnimateStatus(normalizeStatus(item.status))}
                  animationDelay={20}
                  animationColor="#38AAE7"
                  dotsCountValue={3}
                />
              </div>
              <div className="vertical-left">
                <DashedLineComponent
                  color={getColor(normalizeStatus(item.status))}
                  width="60px"
                  height="5px"
                  dashSpacing="5px"
                  shape="vertical"
                  animate={shouldAnimateStatus(normalizeStatus(item.status))}
                  animationDelay={20}
                  animationColor="#38AAE7"
                  dotsCountValue={4}
                />
              </div>
              <div className="horizontal-left">
                <DashedLineComponent
                  color={getColor(normalizeStatus(item.status))}
                  width="5px"
                  height="100px"
                  dashSpacing="10px"
                  shape="horizontal"
                  animate={shouldAnimateStatus(normalizeStatus(item.status))}
                  animationDelay={20}
                  animationColor="#38AAE7"
                  dotsCountValue={6}
                />
              </div>
            </>
          ))}

        {array.length === 2 &&
          firsttwo?.map((item) => (
            <>
              <div className="vertical-right">
                <DashedLineComponent
                  color={getColor(normalizeStatus(item.status))}
                  width="75px"
                  height="5px"
                  dashSpacing="5px"
                  shape="vertical"
                  animate={shouldAnimateStatus(normalizeStatus(item.status))}
                  animationDelay={2000}
                  animationColor="#38AAE7"
                  dotsCountValue={4}
                />
              </div>
              <div className="horizontal-right">
                <DashedLineComponent
                  color={getColor(normalizeStatus(item.status))}
                  width="5px"
                  height="100px"
                  dashSpacing="10px"
                  shape="horizontal"
                  animate={shouldAnimateStatus(normalizeStatus(item.status))}
                  animationDelay={20}
                  animationColor="#38AAE7"
                  dotsCountValue={6}
                />
              </div>
            </>
          ))}
        {array?.length > 0 && (
          <div
            className={`status-diagram ${array?.length === 1 ? 'single-connect' : 'multiple-connect'}`}>
            {array?.map((item, index) => (
              <div key={item._id}>
                <div className={`box ${normalizeStatus(item.status)}`}>C{index + 1}</div>

                <div className={`resonsive-container ${normalizeStatus(item.status)}`}>
                  <div className="car-images">
                    {normalizeStatus(item.status) === 'inprogress' && <CarIconPrimary />}
                    {normalizeStatus(item.status) === 'fault' && <DangerfaultIcon />}
                    {normalizeStatus(item.status) === 'unavailable' && <CarUnAvailable />}
                    {normalizeStatus(item.status) === 'free' && <CarFreeIcon />}
                    {normalizeStatus(item.status) === 'finishing' && <ElectricityFinishing />}
                    {normalizeStatus(item.status) === 'preparing' && <TimePreparing />}
                    {normalizeStatus(item.status) === 'reserved' && <CalendarTickReserved />}
                    {normalizeStatus(item.status) === 'suspendedev' && <SlashSuspendedev />}
                    {normalizeStatus(item.status) === 'suspendedevse' && <SlashSuspendedev />}
                  </div>
                  {normalizeStatus(item.status) === 'inprogress' && (
                    <div className="device-status-container">
                      <div className="static-container" />
                      <div className="animation-container" />
                    </div>
                  )}
                  <div className="resonsive-container-barchart">
                    {normalizeStatus(item.status) === 'inprogr' && (
                      <ResponsiveContainer width="100%" height={80}>
                        <BarChart data={[item]}>
                          <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={false} // Completely disables tick labels
                          />
                          <YAxis domain={[0, 100]} hide />
                          <defs>
                            <linearGradient id={`barGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#2AA700" />
                              <stop offset="100%" stopColor="#A4EB34" />
                            </linearGradient>
                          </defs>
                          <Bar
                            dataKey="value"
                            barSize={50}
                            radius={[6, 6, 6, 6]}
                            fill={`url(#barGradient-${index})`}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                    <div className={`label-${normalizeStatus(item.status)}`}>
                      {getStatusLabel(normalizeStatus(item.status))}
                      <p>
                        {item.value !== undefined && (
                          <span>
                            {item.value >= 60
                              ? `${Math.floor(item.value / 60)}h${item.value % 60 !== 0 ? ` ${item.value % 60}m ` : ''}`
                              : `${item.value}m`}
                            {item.value > 0 && (
                              <span className="charging-text">{t('charging')} </span>
                            )}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {normalizeStatus(item.status) === 'inprogress' && (
                    <div className="user-id">
                      <div className="car-info-brand">
                        {item.vehicleManufacturer && (
                          <div className="row">
                            <div className="brand-label">{t('brand')}</div>
                            <div className="value">{item.vehicleManufacturer} </div>
                          </div>
                        )}
                        {item.vehicleModel && (
                          <div className="row">
                            <div className="brand-label">{t('model')}</div>
                            <div className="value">{item.vehicleModel}</div>
                          </div>
                        )}
                      </div>
                      {item.userCustomId && (
                        <div className="car-info-row">
                          <div className="win-label">{t('userId')}</div>
                          <div className="value link ">{item.userCustomId} </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceCard;
